import { Extension } from '@tiptap/core';

export const MAX_INDENT_LEVEL = 8;

const INDENTABLE_TYPES = ['paragraph', 'listItem', 'blockquote', 'heading'];

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      increaseIndent: () => ReturnType;
      decreaseIndent: () => ReturnType;
    };
  }
}

// A Google-Docs-style paragraph indent, distinct from blockquote: pushes the
// current block's left margin in via a `data-indent-level` attribute/CSS,
// with no quote styling. Deliberately overrides list items' native Tab
// (nest-into-sublist) behavior with the same flat margin push, per product
// decision that indent is a pure spacing control, not list restructuring.
export const Indent = Extension.create({
  name: 'indent',

  addGlobalAttributes() {
    return [
      {
        types: INDENTABLE_TYPES,
        attributes: {
          indentLevel: {
            default: null,
            parseHTML: (element: HTMLElement) => {
              const raw = element.getAttribute('data-indent-level');
              const level = raw ? parseInt(raw, 10) : NaN;
              return Number.isInteger(level) && level >= 1 && level <= MAX_INDENT_LEVEL
                ? level
                : null;
            },
            renderHTML: (attributes: { indentLevel?: number | null }) => {
              if (!attributes.indentLevel) return {};
              return { 'data-indent-level': attributes.indentLevel };
            },
          },
        },
      },
    ];
  },

  addCommands() {
    return {
      increaseIndent:
        () =>
        ({ editor, state }) => {
          const node = state.selection.$from.parent;
          if (!INDENTABLE_TYPES.includes(node.type.name)) return false;

          const current = (node.attrs.indentLevel as number | null) ?? 0;
          if (current >= MAX_INDENT_LEVEL) return false;

          return editor.commands.updateAttributes(node.type.name, {
            indentLevel: current + 1,
          });
        },
      decreaseIndent:
        () =>
        ({ editor, state }) => {
          const node = state.selection.$from.parent;
          if (!INDENTABLE_TYPES.includes(node.type.name)) return false;

          const current = (node.attrs.indentLevel as number | null) ?? 0;
          if (current <= 0) return false;

          return editor.commands.updateAttributes(node.type.name, {
            indentLevel: current - 1 || null,
          });
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Always report the key as handled, even when there's nothing to
      // indent/outdent (already at the limit, or an unsupported node type).
      // ProseMirror's keymap treats a `false` return as "unhandled" and lets
      // the browser run its own default action for the key — for Tab that's
      // moving focus to the next focusable element on the page, which must
      // never happen while the user is typing in the editor.
      Tab: () => {
        this.editor.commands.increaseIndent();
        return true;
      },
      'Shift-Tab': () => {
        this.editor.commands.decreaseIndent();
        return true;
      },
    };
  },
});
